import { z } from 'zod';
import { isValidAvsNumber, normalizeAvsNumber } from '../scripts/avs.validation';
import { optionalString, requiredString, optionalNumber, optionalDate, requiredDate, coerceNumber } from './helpers';

// --- Sub-schemas ---

export const contactSnapshotSchema = z.object({
  name: requiredString(),
  first_name: optionalString(),
  last_name: optionalString(),
  address: z.object({
    street: optionalString().default(''),
    number: optionalString().default(''),
    complement: optionalString(),
    zip: optionalString().default(''),
    city: optionalString().default(''),
    country: optionalString().default('CH'),
  }).optional().default({}),
  email: optionalString(),
  phone: optionalString(),
  language: optionalString().default('fr'),
});

export const PERMIT_TYPES = ['CH', 'B', 'C', 'Ci', 'G', 'L', 'F', 'N', 'S'] as const;

export const permitSchema = z.object({
  type: z.enum(PERMIT_TYPES),
  number: optionalString(),
  valid_until: optionalDate(),
  issuing_canton: optionalString(),
});

export const residenceSchema = z.object({
  canton: optionalString(),
  municipality: optionalString(),
  municipality_bfs: optionalString(),
  country: requiredString().default('CH'),
});

export const spouseSchema = z.object({
  has_spouse: z.boolean().default(false),
  is_working: z.boolean().default(false),
  work_canton: optionalString(),
  work_country: optionalString(),
});

export const childSchema = z.object({
  first_name: requiredString(),
  last_name: requiredString(),
  birth_date: requiredDate(),
  allowance_from: optionalDate(),
  allowance_to: optionalDate(),
  in_education: z.boolean().default(false),
});

export const paymentSchema = z.object({
  iban: optionalString(),
  bic: optionalString(),
  holder_name: optionalString(),
});

export const GENDERS = ['male', 'female'] as const;
export const CIVIL_STATUSES = ['single', 'married', 'divorced', 'widowed', 'registered_partnership', 'dissolved_partnership'] as const;

// --- Main employee schema ---

export const employeeSchema = z.object({
  id_contact: requiredString(),
  contact_snapshot: contactSnapshotSchema,
  avs_number: z.string()
    .min(1)
    .transform(normalizeAvsNumber)
    .refine(isValidAvsNumber, { message: 'avsNumberInvalid' }),
  birth_date: requiredDate().refine((val) => {
    const d = new Date(val);
    if (isNaN(d.getTime())) return false;
    const now = new Date();
    const age = now.getFullYear() - d.getFullYear();
    return d < now && age >= 15;
  }, { message: 'birthDateInvalid' }),
  gender: z.enum(GENDERS),
  civil_status: z.enum(CIVIL_STATUSES),
  civil_status_since: optionalDate(),
  nationality: requiredString(),
  permit: permitSchema.default({ type: 'CH' }),
  residence: residenceSchema.default({ country: 'CH' }),
  work_canton: requiredString(),
  spouse: spouseSchema.default({ has_spouse: false, is_working: false }),
  children: z.array(childSchema).default([]),
  payment: paymentSchema.default({}),
  notes: optionalString(),
}).superRefine((data, ctx) => {
  // Permit rules: non-CH permits require valid_until
  if (data.permit.type !== 'CH') {
    if (!data.permit.valid_until) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'permitValidUntilRequired',
        path: ['permit', 'valid_until'],
      });
    }
  }

  // Residence rules: CH residence requires canton
  if (data.residence.country === 'CH') {
    if (!data.residence.canton) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'residenceCantonRequired',
        path: ['residence', 'canton'],
      });
    }
  }

  // Spouse rules
  if (data.spouse.has_spouse) {
    if (data.spouse.is_working && !data.spouse.work_canton && !data.spouse.work_country) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'spouseWorkLocationRequired',
        path: ['spouse', 'work_canton'],
      });
    }
  }

  // Children: allowance_to must be after allowance_from
  data.children.forEach((child, i) => {
    if (child.allowance_from && child.allowance_to) {
      if (new Date(child.allowance_to) <= new Date(child.allowance_from)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'allowanceToAfterFrom',
          path: ['children', i, 'allowance_to'],
        });
      }
    }
  });
});

export type EmployeeFormData = z.input<typeof employeeSchema>;
export type EmployeePayload = z.output<typeof employeeSchema>;
